import axios from 'axios';
import {
  ACCESS_TOKEN_LOCAL_STORAGE_KEY,
  azureClientId,
  msalInstance,
} from '../components/auth/authConfig';

// ----------------------------------------------------------------------

const axiosInstance = axios.create({
  baseURL: '/api',
});

// Track if we're currently refreshing a token to avoid multiple refresh attempts
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve(token);
    }
  });

  failedQueue = [];
};

const refreshToken = async () => {
  try {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      return null;
    }

    const account = accounts[0];
    const tokenResponse = await msalInstance.acquireTokenSilent({
      account,
      scopes: [`api://${azureClientId}/access_as_user`],
    });

    if (tokenResponse && tokenResponse.accessToken) {
      localStorage.setItem(
        ACCESS_TOKEN_LOCAL_STORAGE_KEY,
        tokenResponse.accessToken,
      );
      axiosInstance.defaults.headers.common[
        'Authorization'
      ] = `Bearer ${tokenResponse.accessToken}`;
      return tokenResponse.accessToken;
    }
    return null;
  } catch (error) {
    console.error('Failed to refresh token:', error);
    return null;
  }
};

axiosInstance.interceptors.request.use(async (config) => {
  const token = localStorage.getItem(ACCESS_TOKEN_LOCAL_STORAGE_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosInstance.interceptors.response.use(
  (res) => res,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If we're already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers['Authorization'] = `Bearer ${token}`;
            return axiosInstance.request(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshToken();
        if (newToken) {
          processQueue(null, newToken);
          originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
          return axiosInstance.request(originalRequest);
        } else {
          processQueue(new Error('Failed to refresh token'), null);
          // If refresh fails, redirect to login
          window.location.href = '/login';
          return Promise.reject(error);
        }
      } catch (refreshError) {
        processQueue(refreshError, null);
        // If refresh fails, redirect to login
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default axiosInstance;
// ----------------------------------------------------------------------

export const fetcher = async (args) => {
  const [url, config] = Array.isArray(args) ? args : [args];

  const res = await axiosInstance.get(url, { ...config });

  return res.data;
};
